/**
 * IncidentsDashboard.tsx — Incident Management dashboard (/dashboard/incidents)
 *
 * Purpose:    Full incident lifecycle (open → acknowledge → resolve), runbook viewer,
 *             post-mortem editor, and per-incident timeline.
 * Inputs:     useIncidentData hook (port 3833)
 * Outputs:    CreateIncidentForm + IncidentDetail + incident list
 * Constraints: 7-state AsyncScreen; severity badges; no external markdown renderer
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useState } from 'react'
import { AsyncScreen } from '../../components/AsyncScreen'
import { useIncidentData } from '../../hooks/useIncidentData'
import { CreateIncidentForm } from './CreateIncidentForm'
import { IncidentDetail, SeverityBadge, STATUS_LABEL } from './IncidentDetail'

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function IncidentsSkeleton() {
  return (
    <div className="animate-pulse space-y-3 p-6" aria-hidden>
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-16 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function IncidentsDashboard() {
  const { state, data, error, refresh, acknowledgeIncident, resolveIncident, createIncident } =
    useIncidentData()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const openIncidents = data?.incidents.filter((i) => i.status !== 'resolved') ?? []
  const resolvedIncidents = data?.incidents.filter((i) => i.status === 'resolved') ?? []

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-ink">Incidents</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Manage incident lifecycle, runbooks, and post-mortems
          </p>
        </div>
        <button
          type="button"
          onClick={() => { void refresh() }}
          className="rounded-md border border-brand-card px-3 py-1.5 text-sm text-brand-secondary hover:bg-brand-surface-2"
        >
          Refresh
        </button>
      </div>

      <div className="mb-6">
        <CreateIncidentForm onCreate={createIncident} />
      </div>

      <AsyncScreen
        state={state}
        error={error}
        onRetry={() => { void refresh() }}
        loadingSkeleton={<IncidentsSkeleton />}
        emptyMessage="No active incidents"
        emptyAction={<span className="text-sm text-brand-muted">No active incidents — great job!</span>}
      >
        {data && (
          <div className="space-y-6">
            {openIncidents.length > 0 && (
              <section>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  Active ({openIncidents.length})
                </h2>
                <div className="space-y-3">
                  {openIncidents.map((inc) => (
                    <div key={inc.id}>
                      <button
                        type="button"
                        className="mb-1 flex w-full items-center gap-2 rounded-md border border-brand-subtle bg-brand-surface/50 px-4 py-3 text-left hover:bg-brand-surface-2/60"
                        onClick={() =>
                          setSelectedId((prev) => (prev === inc.id ? null : inc.id))
                        }
                      >
                        <SeverityBadge severity={inc.severity} />
                        <span className="flex-1 font-medium text-brand-ink">{inc.title}</span>
                        <span className={`text-sm ${STATUS_LABEL[inc.status].className}`}>
                          {STATUS_LABEL[inc.status].label}
                        </span>
                      </button>
                      {selectedId === inc.id && (
                        <IncidentDetail
                          incident={inc}
                          onAcknowledge={acknowledgeIncident}
                          onResolve={resolveIncident}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {resolvedIncidents.length > 0 && (
              <section>
                <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-secondary">
                  Resolved ({resolvedIncidents.length})
                </h2>
                <div className="space-y-2">
                  {resolvedIncidents.slice(0, 10).map((inc) => (
                    <div
                      key={inc.id}
                      className="flex items-center justify-between rounded-md border border-brand-subtle px-4 py-2 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <SeverityBadge severity={inc.severity} />
                        <span className="text-brand-secondary">{inc.title}</span>
                      </div>
                      <time className="text-xs text-brand-muted">
                        {inc.resolvedAt ? new Date(inc.resolvedAt).toLocaleDateString() : '—'}
                      </time>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </AsyncScreen>
    </div>
  )
}
